import React from 'react';
import { Camera, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { resolveDocumentDirection } from '@/i18n/languages';

interface SelfiePermissionScreenProps {
  onContinue: () => void;
  onSkip: () => void;
  permissionDenied?: boolean;
}

/**
 * Pre-permission screen shown before requesting camera access.
 * Apple 5.1.1(iv): no Settings redirect or pressure copy after denial.
 */
export const SelfiePermissionScreen: React.FC<SelfiePermissionScreenProps> = ({
  onContinue,
  onSkip,
  permissionDenied = false,
}) => {
  const { t, i18n } = useTranslation();
  const direction = resolveDocumentDirection(i18n.resolvedLanguage || i18n.language);

  return (
    <div dir={direction} className="fixed inset-0 z-50 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden pt-[calc(env(safe-area-inset-top,0px)+1rem)]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/20 via-slate-900 to-black"></div>
      <div className="absolute top-1/4 start-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 end-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

      <div className="relative z-10 w-full max-w-md space-y-8 text-center">
        <div className="flex justify-center">
          <div className="w-24 h-24 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-pink-500/25">
            <Camera className="w-12 h-12 text-white" />
          </div>
        </div>

        <div className="space-y-4 text-start">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            {t('camera.addProfilePhotoTitle')}
          </h2>
          {permissionDenied ? (
            <p className="text-slate-300 text-lg px-4 text-start">
              {t('camera.cameraDeniedMessage')}
            </p>
          ) : (
            <>
              <p className="text-slate-300 text-lg px-4 text-start">
                {t('camera.addProfilePhotoDesc')}
              </p>
              <p className="text-slate-400 text-sm px-4 text-start">
                {t('camera.cameraOptionalHint')}
              </p>
            </>
          )}
        </div>

        <div className="space-y-3 pt-4">
          {permissionDenied ? (
            <>
              <Button
                onClick={onSkip}
                className="w-full h-14 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-pink-500/25 transition-all duration-300 transform hover:scale-[1.02]"
                size="lg"
              >
                {t('camera.continueWithoutPhoto')}
              </Button>
              <button
                type="button"
                onClick={onSkip}
                className="w-full text-slate-400 hover:text-slate-300 text-sm font-medium transition-colors py-2"
              >
                {t('verified.actions.maybeLater')}
              </button>
            </>
          ) : (
            <Button
              onClick={onContinue}
              className="w-full h-14 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-pink-500/25 transition-all duration-300 transform hover:scale-[1.02]"
              size="lg"
            >
              {t('camera.continue')}
              <ArrowRight className="ms-2 w-5 h-5 rtl:rotate-180" aria-hidden />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
